import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { CheckCircle2, FileText, Search, Shield, Send, UserCheck, Scale, FolderOpen, ClipboardList, Gavel } from 'lucide-react';

const PricingSection = ({ onOpenRegistration, packages: customPackages }) => {
  const [activeTab, setActiveTab] = useState('self');
  const selfFilingStepsRef = useRef(null);
  const attorneyStepsRef = useRef(null);

  // Auto-scroll self filing steps on mobile
  useEffect(() => {
    const interval = setInterval(() => {
      if (selfFilingStepsRef.current && window.innerWidth < 768) {
        const scrollWidth = selfFilingStepsRef.current.scrollWidth;
        const clientWidth = selfFilingStepsRef.current.clientWidth;
        const currentScroll = selfFilingStepsRef.current.scrollLeft;
        const cardWidth = clientWidth * 0.85;
        
        if (currentScroll + clientWidth >= scrollWidth - 10) {
          selfFilingStepsRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          selfFilingStepsRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
        }
      }
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll attorney steps on mobile
  useEffect(() => {
    const interval = setInterval(() => {
      if (attorneyStepsRef.current && window.innerWidth < 768) {
        const scrollWidth = attorneyStepsRef.current.scrollWidth;
        const clientWidth = attorneyStepsRef.current.clientWidth;
        const currentScroll = attorneyStepsRef.current.scrollLeft;
        const cardWidth = clientWidth * 0.85;
        
        if (currentScroll + clientWidth >= scrollWidth - 10) {
          attorneyStepsRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          attorneyStepsRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' });
        }
      }
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  // Tab 1: Self Filing with Assistance - Step Cards
  const selfFilingSteps = [
    {
      title: 'Application Preparation',
      icon: FileText,
      description: 'Trademark Upgrade reviews your filing information, identifies the correct trademark class, and prepares your application for submission through the USPTO TEAS system.'
    },
    {
      title: 'Identity Review and Approval',
      icon: UserCheck,
      description: 'You complete ID.me verification, then check the attorney-prepared application in detail. Any required edits or changes can be requested before submission.'
    },
    {
      title: 'USPTO Filing',
      icon: Send,
      description: 'After approval, Trademark Upgrade submits your application to the USPTO, ensuring every detail is accurate, complete, and ready for official review.'
    }
  ];

  // Tab 2: IP Attorney-Led Filing - Step Cards
  const attorneySteps = [
    {
      title: 'Information Collection',
      icon: ClipboardList,
      description: 'We collect your trademark details, including name, logo (if applicable), and a brief description of your products/services.'
    },
    {
      title: 'Attorney Review & Application Drafting',
      icon: Gavel,
      description: 'Our IP attorney reviews your case, determines the appropriate trademark class(es), and prepares a strong, USPTO-compliant application.'
    },
    {
      title: 'Filing & Case Management',
      icon: FolderOpen,
      description: 'We file the application on your behalf and handle all procedural steps, including monitoring your application and managing any USPTO correspondence.'
    }
  ];

  // Default pricing packages for Self Filing tab
  const selfFilingPackages = customPackages?.selfFiling || [
    {
      name: 'Basic',
      price: '$45 + Federal Filing Fee',
      subtitle: 'Simple guided process with expert support at every step to ensure accuracy',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
        'Case Review',
        'Case Filing',
        'Basic Search'
      ]
    },
    {
      name: 'Deluxe',
      price: '$149 + Federal Filing Fee',
      subtitle: 'Balanced assistance that helps you move forward with clear professional direction',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'Case Review',
        'Case Filing',
        'Direct Hit Search'
      ]
    },
    {
      name: 'Premium',
      price: '$395 + Federal Filing Fee',
      popular: true,
      subtitle: 'Advanced support with deeper planning, careful review, and ongoing clarity',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'Case Filing',
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert'
      ]
    },
    {
      name: 'Elite',
      price: '$595 + Federal Filing Fee',
      subtitle: 'Premium guided service with expert attention, leadership, and complete confidence',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured'
      ]
    }
  ];

  // Default pricing packages for Attorney-Led tab (independent set)
  const attorneyPackages = customPackages?.attorneyLed || [
    {
      name: 'Basic',
      price: '$795 + Federal Filing Fee',
      subtitle: 'Reliable professional support for a smoother and more confident filing process',
      features: [
        'Attorney-Handled Trademark Filing from Start to Finish',
        'Professional Case Evaluation',
        'Application Preparation & Submission',
        'Preliminary Trademark Search',
        'State-Level E-Filing Included',
        'Attorney-Handled Trademark Filing from Start to Finish',
        'Professional Case Evaluation',
        'Application Preparation & Submission',
        'Preliminary Trademark Search',
        'State-Level E-Filing Included'
      ]
    },
    {
      name: 'Deluxe',
      price: '$895 + Federal Filing Fee',
      popular: true,
      subtitle: 'Guided step-by-step assistance with added care, clarity, and peace of mind',
      features: [
        'Attorney-Led Trademark Filing',
        'End-to-End Case Handling',
        'Comprehensive Case Review & Analysis',
        'Professional Application Preparation',
        'Trademark Secured',
        'USPTO-Compliant Filing Process',
        'Attorney-Led Trademark Filing',
        'End-to-End Case Handling',
        'Comprehensive Case Review & Analysis'
      ]
    },
    {
      name: 'Premium',
      price: '$1145 + Federal Filing Fee',
      subtitle: 'Organized service with expert review, careful preparation, and steady progress',
      features: [
        'Attorney-Led Case Review',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Trademark Monitoring & Alerts',
        'Comprehensive Risk & Refusal Meter',
        'Attorney-Led Case Review',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Trademark Monitoring & Alerts',
        'Comprehensive Risk & Refusal Meter'
      ]
    },
    {
      name: 'Elite',
      price: '$1545',
      subtitle: 'Priority support with focused attention, quicker handling, and greater confidence',
      features: [
        'Attorney-Led Case Review & Strategy',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Federal & State Trademark Search Report',
        'Trademark Monitoring & Real-Time Alerts',
        'Attorney-Led Case Review & Strategy',
        'Complete Case Filing & Management',
        'Advanced Direct-Hit Trademark Search',
        'Federal & State Trademark Search Report',
        'Trademark Monitoring & Real-Time Alerts'
      ]
    }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleRegisterClick = (pkg = null) => {
    // Pass tmSignUpType based on active tab: 1 for Self Filing, 2 for Attorney-Led
    const tmSignUpType = activeTab === 'self' ? 1 : 2;
    // Default to Basic/$45 if no package specified
    const packageTitle = pkg?.name || 'Basic';
    // Extract numeric price value (handles "$45 + Federal Filing Fee" format)
    const priceMatch = pkg?.price?.match(/\$([0-9]+)/);
    const packageAmount = priceMatch ? parseInt(priceMatch[1]) : 45;
    onOpenRegistration(tmSignUpType, packageTitle, packageAmount);
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Intro Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Brand Protection Made Simple
          </h2>
          <p className="text-base text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Trademark Upgrade helps businesses secure their identity through reliable filing, monitoring, renewals, and complete trademark support. Whether your goal is to protect a company name, claim ownership of a logo, or safeguard a slogan, our team makes the process clear and manageable. We assist brands across all 50 U.S. states and international markets, helping keep your intellectual property protected wherever your business operates.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-white p-1 rounded-xl shadow-md border border-gray-200 inline-flex">
            <button
              onClick={() => handleTabChange('self')}
              className={`px-6 py-3 rounded-lg font-semibold text-sm md:text-base transition-all duration-300 ${
                activeTab === 'self'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
              }`}
              aria-selected={activeTab === 'self'}
              role="tab"
            >
              Self Filing with Assistance
            </button>
            <button
              onClick={() => handleTabChange('attorney')}
              className={`px-6 py-3 rounded-lg font-semibold text-sm md:text-base transition-all duration-300 ${
                activeTab === 'attorney'
                  ? 'bg-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
              }`}
              aria-selected={activeTab === 'attorney'}
              role="tab"
            >
              IP Attorney-Led Filing
            </button>
          </div>
        </div>

        {/* Tab Content with Animation */}
        <div className="transition-all duration-500 ease-in-out">
          {activeTab === 'self' ? (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Step Cards - Desktop Grid */}
              <div className="hidden md:grid md:grid-cols-3 gap-6 mb-12">
                {selfFilingSteps.map((step, index) => (
                  <Card key={index} className="border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 hover:shadow-lg">
                    <CardContent className="pt-6 pb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mb-4">
                        <step.icon className="h-7 w-7 text-orange-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Step Cards - Mobile Slider */}
              <div ref={selfFilingStepsRef} className="md:hidden flex overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 mb-12">
                {selfFilingSteps.map((step, index) => (
                  <Card key={index} className="border-2 border-gray-200 snap-center flex-shrink-0 w-[85vw]">
                    <CardContent className="pt-6 pb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mb-4">
                        <step.icon className="h-7 w-7 text-orange-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pricing Section Heading */}
              <div className="text-center mb-10">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Self-Filing with Expert Assistance and Complete
                </h3>
                <p className="text-base text-gray-600">
                  Support Plan for Easy Filing
                </p>
              </div>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {selfFilingPackages.map((pkg, index) => (
                  <Card key={index} className={`border-2 ${pkg.popular ? 'border-orange-600 shadow-xl' : 'border-gray-200'} relative h-[580px] flex flex-col`}>
                    {pkg.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-orange-600 text-white text-sm font-semibold px-4 py-1 rounded-full">
                          Recommended
                        </span>
                      </div>
                    )}
                    <CardContent className="pt-8 flex flex-col h-full">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                      <div className="text-3xl font-bold text-orange-600 mb-3">{pkg.price.replace(' + Federal Filing Fee', '')}</div>
                      <p className="text-sm text-gray-600 mb-4 h-[40px]">{pkg.subtitle}</p>
                      
                      <div className="flex-1 overflow-y-auto mb-4 pr-2 mt-5" style={{ maxHeight: '280px' }}>
                        <ul className="space-y-2">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-xs text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <p className="text-xs text-gray-500 italic mb-4">(Govt. filing fee not included)</p>
                        <Button 
                          onClick={() => handleRegisterClick(pkg)} 
                          className={`w-full ${pkg.popular ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}
                        >
                          Register My Trademark
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Step Cards - Desktop Grid */}
              <div className="hidden md:grid md:grid-cols-3 gap-6 mb-12">
                {attorneySteps.map((step, index) => (
                  <Card key={index} className="border-2 border-gray-200 hover:border-orange-300 transition-all duration-300 hover:shadow-lg">
                    <CardContent className="pt-6 pb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mb-4">
                        <step.icon className="h-7 w-7 text-orange-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Step Cards - Mobile Slider */}
              <div ref={attorneyStepsRef} className="md:hidden flex overflow-x-auto gap-6 snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4 mb-12">
                {attorneySteps.map((step, index) => (
                  <Card key={index} className="border-2 border-gray-200 snap-center flex-shrink-0 w-[85vw]">
                    <CardContent className="pt-6 pb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center mb-4">
                        <step.icon className="h-7 w-7 text-orange-600" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pricing Section Heading */}
              <div className="text-center mb-10">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Attorney Led Filing with Complete
                </h3>
                <p className="text-base text-gray-600">
                  End-to-End Service Plan
                </p>
              </div>

              {/* Pricing Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {attorneyPackages.map((pkg, index) => (
                  <Card key={index} className={`border-2 ${pkg.popular ? 'border-orange-600 shadow-xl' : 'border-gray-200'} relative h-[580px] flex flex-col`}>
                    {pkg.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-orange-600 text-white text-sm font-semibold px-4 py-1 rounded-full">
                          Recommended
                        </span>
                      </div>
                    )}
                    <CardContent className="pt-8 flex flex-col h-full">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                      <div className="text-3xl font-bold text-orange-600 mb-3">{pkg.price.replace(' + Federal Filing Fee', '')}</div>
                      <p className="text-sm text-gray-600 mb-4 h-[40px]">{pkg.subtitle}</p>
                      
                      <div className="flex-1 overflow-y-auto mb-4 pr-2 mt-5" style={{ maxHeight: '280px' }}>
                        <ul className="space-y-2">
                          {pkg.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                              <span className="text-xs text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="mt-6 pt-4 border-t border-gray-100">
                        <p className="text-xs text-gray-500 italic mb-4">
                          {pkg.name === 'Elite' ? '(Govt. filing fee included)' : '(Govt. filing fee not included)'}
                        </p>
                        <Button 
                          onClick={() => handleRegisterClick(pkg)} 
                          className={`w-full ${pkg.popular ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}
                        >
                          Register My Trademark
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CTA Box */}
        <div className="mt-12 max-w-4xl mx-auto">
          <Card className="bg-gradient-to-r from-orange-600 to-amber-600 border-0 shadow-2xl h-[580px] flex flex-col">
            <CardContent className="pt-12 pb-12 px-8 flex flex-col justify-center items-center text-center h-full">
              <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-6">
                <Shield className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Need Help Choosing?
              </h3>
              <p className="text-lg md:text-xl text-orange-100 mb-8 max-w-2xl">
                Our trademark experts are here to help you select the perfect package for your business needs. Get a free consultation today!
              </p>
              <div className="space-y-4 mb-8 text-left max-w-md">
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-white flex-shrink-0" />
                  <span className="text-white">Free expert consultation</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-white flex-shrink-0" />
                  <span className="text-white">Personalized package recommendation</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-white flex-shrink-0" />
                  <span className="text-white">No obligation, no pressure</span>
                </div>
              </div>
              <Button 
                size="lg"
                onClick={handleRegisterClick}
                className="bg-white text-orange-600 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 h-auto"
              >
                <Scale className="mr-2 h-5 w-5" />
                Talk to an Expert Now
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
